/**
 * SetOperatorBar — the separator strip between two set-operator branches: a
 * horizontal rule either side of the kind badge. Once the parent statement's
 * edit context is ready (schema snapshot loaded) the badge becomes a real
 * `SetOperatorKindDropdown`; otherwise it renders the disabled § 1.4 scaffold
 * with a reason-specific tooltip — `setOperatorNotReady` when an editContext
 * exists but the snapshot hasn't loaded yet, `editingUnavailable` when no
 * editContext is wired at all (mirrors `removeColumnNotReady`'s two-state
 * copy). Split out of `SetOperatorBranches.tsx` to keep that file under the
 * ~200-line budget once the dropdown wiring landed (§2.3 S7).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — set-operator switch (§2.3 S7).
 */
import { ChevronsUpDown } from 'lucide-react';

import { cn } from '../../../../../lib/utils';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeTooltip } from '../common/VqeTooltip';
import type { SetOperatorSeparator } from './extractSetOperatorBranches';
import { KIND_BADGE_CLASS } from './setOperatorKindMaps';
import { SetOperatorKindDropdown } from './SetOperatorKindDropdown';

export interface SetOperatorBarProps {
  readonly separator: SetOperatorSeparator;
  readonly index: number;
  readonly options: ParseOptions;
  readonly editContext?: VqeEditContext;
}

const TOOLTIP_DISABLED = vqeCopy.disabled.editingUnavailable;
const TOOLTIP_NOT_READY = vqeCopy.disabled.setOperatorNotReady;

export function SetOperatorBar({ separator, index, options, editContext }: SetOperatorBarProps) {
  const testId = `vqe-set-operator-bar-${index}`;
  const label = separator.isAll ? `${separator.kind} ALL` : separator.kind;
  const editReady = !!editContext && !!editContext.service.getSchemaSnapshot();

  return (
    <div
      data-testid={testId}
      data-kind={separator.kind}
      data-all={separator.isAll ? 'true' : 'false'}
      className="flex items-center gap-2 px-2"
    >
      <div className="h-px flex-1 bg-border" aria-hidden />
      {editReady && editContext ? (
        <SetOperatorKindDropdown
          currentKind={separator.kind}
          currentAll={separator.isAll}
          branchIndex={index}
          editContext={editContext}
          options={options}
          testIdPrefix={testId}
        />
      ) : (
        <VqeTooltip content={editContext ? TOOLTIP_NOT_READY : TOOLTIP_DISABLED}>
          <button
            type="button"
            disabled
            aria-disabled
            data-testid={`${testId}-kind-dropdown`}
            className={cn(
              'inline-flex items-center gap-1 rounded-md border px-2.5 py-0.5 text-[11px] font-mono font-semibold uppercase tracking-wide cursor-default disabled:opacity-100',
              KIND_BADGE_CLASS[separator.kind],
            )}
          >
            <span data-testid={`${testId}-label`}>{label}</span>
            <ChevronsUpDown className="h-3 w-3 opacity-50" aria-hidden />
          </button>
        </VqeTooltip>
      )}
      <div className="h-px flex-1 bg-border" aria-hidden />
    </div>
  );
}
